import { ApolloClient, InMemoryCache } from '@apollo/client';
import type { GraphQLError } from 'graphql';

import { get as getCookie } from 'js/lib/cookie';
import { getIetfLanguageTag } from 'js/lib/language';

import type {
  AiCoachPlatform_TextStreamingChatSubscription as TextStreamingChatSubscription,
  AiCoachPlatform_TextStreamingChatSubscriptionVariables as TextStreamingChatSubscriptionVariables,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachTextStreamingChat';
import type { CoachRecommendedActionFragment } from 'bundles/ai-coach-platform/queries/__generated__/coachRecommendedActionFragment';
import AiCoachTextStreamingChatSubscription from 'bundles/ai-coach-platform/queries/aiCoachTextStreamingChat.graphql';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';
import SSELink from 'bundles/page/lib/SSELink';

export type NavigationButtonFragment = {
  navigationLeadingMessage?: string | null;
  buttonLabel?: string | null;
};

export type SubscriptionInput = {
  onChange?: (message: string, messageId: string) => void;
  onComplete?: ({
    finalMessage,
    recommendedActions,
    navigationButtons,
    messageId,
    chatSessionId,
  }: {
    finalMessage: string;
    recommendedActions: CoachRecommendedActionFragment[];
    navigationButtons: NavigationButtonFragment[];
    messageId: string;
    chatSessionId: string | null;
  }) => void;
  onError?: (error: GraphQLError) => void;
};

declare const COURSERA_APP_NAME: string;
declare const COURSERA_APP_VERSION: string;

const createSubscriptionClient = () => {
  const csrf3Token = getCookie('CSRF3-Token');
  const cache = new InMemoryCache();
  const client = new ApolloClient({
    link: new SSELink({
      url: '/dgs/ai-coach-application/subscriptions',
      credentials: 'same-origin',
      headers: {
        'Accept-Language': getIetfLanguageTag(),
        'R2-APP-VERSION': COURSERA_APP_VERSION,
        'X-Coursera-Application': COURSERA_APP_NAME,
        'X-Coursera-Version': COURSERA_APP_VERSION,
        'apollographql-client-name': COURSERA_APP_NAME,
        'apollographql-client-version': COURSERA_APP_VERSION,
        'X-CSRF3-Token': csrf3Token ?? '',
      },
    }),
    cache,
  });

  return client;
};

export const createTextSubscription = ({
  chatSessionId = null,
  courseraContext,
  message,
  config,
  additionalContext,
  configOverride,
}: TextStreamingChatSubscriptionVariables['input']) => {
  const client = createSubscriptionClient();

  if (chatSessionId === '') {
    captureMessageInSentry({
      error: new Error('Found empty string chat session id'),
      source: 'createTextSubscription',
      extra: { chatSessionId },
      level: 'info',
    });
  }

  return ({ onChange, onComplete, onError }: SubscriptionInput) => {
    const input: TextStreamingChatSubscriptionVariables['input'] = {
      additionalContext,
      chatSessionId,
      config,
      configOverride,
      courseraContext,
      message,
    };

    let finalMessage = '';
    let messageId = '';
    // for new chats, `chatSessionId` is null so this sets it to the BE-returned value when available.
    let chatSessionIdNew = chatSessionId;
    let recommendedActions: CoachRecommendedActionFragment[] = [];
    let navigationButtons: NavigationButtonFragment[] = [];

    const observable = client.subscribe<TextStreamingChatSubscription, TextStreamingChatSubscriptionVariables>({
      query: AiCoachTextStreamingChatSubscription,
      variables: { input },
    });

    return observable.subscribe({
      next: async ({ data, errors, ...args }) => {
        const isLastChunk = data?.AiCoach_TextStreamingChat?.isLastChunk;

        // set new chatSessionId if it's not already set for new chats
        if (chatSessionId === null && chatSessionIdNew === null && data?.AiCoach_TextStreamingChat?.chatSessionId) {
          chatSessionIdNew = data?.AiCoach_TextStreamingChat?.chatSessionId;
        }

        // the last chunk contains the aggregated response, so we use that instead of appending,
        // and it also contains the recommended actions and navigation buttons
        if (isLastChunk) {
          finalMessage = data?.AiCoach_TextStreamingChat?.messageByAiCoach?.message ?? finalMessage;
          recommendedActions = data?.AiCoach_TextStreamingChat?.recommendedActions ?? [];
          navigationButtons = data?.AiCoach_TextStreamingChat?.navigationButtons ?? [];
          messageId = data?.AiCoach_TextStreamingChat?.messageByAiCoach?.messageId ?? messageId;
          return;
        }

        messageId = data?.AiCoach_TextStreamingChat?.messageByAiCoach?.messageId ?? messageId;
        finalMessage += data?.AiCoach_TextStreamingChat?.messageByAiCoach?.message ?? '';

        onChange?.(finalMessage, messageId);
      },
      complete: () => {
        onComplete?.({
          finalMessage,
          recommendedActions,
          navigationButtons,
          messageId,
          chatSessionId: chatSessionIdNew,
        });
      },
      error: onError,
    });
  };
};
